import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { login, clearError } from '../store/slices/authSlice';
import { addToast } from '../store/slices/toastsSlice';

interface LoginFormData {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, user } = useAppSelector(state => state.auth);

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Get the intended destination from location state, default to '/'
  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    // Redirect based on role if authenticated
    if (isAuthenticated && user) {
      // Team users always go to /workflow, admins go to intended destination
      const destination = user.role === 'team' ? '/workflow' : from;
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  const handleFieldChange = (field: keyof LoginFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear field error when user corrects it
    if (attemptedSubmit && validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.password.trim()) {
      errors.password = 'Password is required';
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setAttemptedSubmit(true);
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const result = await dispatch(login(formData)).unwrap();
      dispatch(addToast('Login successful', 'success'));
      // Redirect based on role
      const destination = result.user.role === 'team' ? '/workflow' : from;
      navigate(destination, { replace: true });
    } catch (err) {
      dispatch(addToast(
        error || 'Login failed. Please check your credentials.',
        'error'
      ));
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Login</h1>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => handleFieldChange('email', e.target.value)}
              style={{
                ...styles.input,
                ...(attemptedSubmit && validationErrors.email ? styles.inputError : {})
              }}
              placeholder="you@example.com"
              disabled={loading}
            />
            {attemptedSubmit && validationErrors.email && (
              <div style={styles.error}>{validationErrors.email}</div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={e => handleFieldChange('password', e.target.value)}
              style={{
                ...styles.input,
                ...(attemptedSubmit && validationErrors.password ? styles.inputError : {})
              }}
              placeholder="••••••••"
              disabled={loading}
            />
            {attemptedSubmit && validationErrors.password && (
              <div style={styles.error}>{validationErrors.password}</div>
            )}
          </div>

          {error && (
            <div style={styles.errorBanner}>
              {error}
            </div>
          )}

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '32px',
    textAlign: 'center'
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease'
  },
  inputError: {
    borderColor: '#dc3545'
  },
  error: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '6px'
  },
  errorBanner: {
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '16px',
    color: '#c33',
    fontSize: '14px',
    textAlign: 'center'
  },
  button: {
    padding: '12px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.2s ease'
  },
  buttonDisabled: {
    backgroundColor: '#90caf9',
    cursor: 'not-allowed'
  }
};
